import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Camera,
  CheckCircle2,
  ChevronLeft,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import { api } from '../../services/api';
import { sitesService } from '../../services/sitesService';
import { useInspectionSchema } from '../../hooks/useInspectionSchema';
import { useStageDateEditing } from './hooks/useStageDateEditing';
import { StageTable } from './components/StageTable';
import { InspectionDateField } from './components/InspectionDateField';
import { buildLiteFollowUps, withInspectionId } from './liteSubmitReplay';
import { openDatePicker } from '@/utils/datePicker';

type OpenCA = {
  id: string;
  question_text?: string | null;
  location?: string | null;
  notes?: string | null;
  action_description?: string | null;
  status: string;
};

type NewCA = {
  id: string;
  location: string;
  notes: string;
  deadline: string;
};

const FORM_ACCEPT = 'image/*,application/pdf,.pdf';

function newCa(): NewCA {
  return {
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `ca-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    location: '',
    notes: '',
    deadline: '',
  };
}

/**
 * Manual-upload inspection (JIM-0904-11). Jim: "It needs to be in the Lite
 * side… it would just take you to a thing right here that says upload the
 * inspection report… I need to stay to the construction on there, and I need
 * all questions gone, but I need a way to do corrective actions."
 *
 * Attach the jurisdiction's own form (photo or PDF), record the stage dates
 * and any corrective actions, and the visit lands in the site history and
 * the CA log like any other — without an EnviroLink PDF. "Send to Client"
 * emails their form plus our Corrective Action Log. Online only: the form
 * has to upload.
 */
export function LiteManualUploadPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [conductedDate, setConductedDate] = useState(
    new Date().toLocaleDateString('en-CA')
  );
  const [formFile, setFormFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [newCas, setNewCas] = useState<NewCA[]>([]);
  const [resolvedIds, setResolvedIds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<null | 'saving' | 'sending'>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<null | {
    inspectionId: string;
    sent?: { sent_count: number; failed_count: number };
  }>(null);

  const { data: site } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesService.getSite(siteId!),
    enabled: !!siteId,
    retry: 1,
  });

  const { data: extractedStagesData } = useQuery({
    queryKey: ['extracted-stages-site', siteId],
    queryFn: async () => {
      const res = await api.get<{ items: any[] }>(
        `/extracted-stages?site_id=${siteId}&size=100`
      );
      return res.data.items || [];
    },
    enabled: !!siteId,
  });

  const { schema } = useInspectionSchema(siteId);
  const allStages = useMemo(() => {
    if (!schema?.stages?.length) return [];
    return schema.stages
      .filter((s) => s.construction_stage_id && s.construction_stage_name)
      .map((s) => ({
        id: s.construction_stage_id as string,
        stage_name: s.construction_stage_name as string,
        stage_order: s.sort_order,
        project_type: schema.project_type as string,
      }))
      .sort((a, b) => a.stage_order - b.stage_order);
  }, [schema]);
  const currentStage = (site as any)?.current_construction_stage ?? null;

  const {
    selectedStageId,
    stageDates,
    setStageDates,
    handleStageDateSetAndSave,
    handleStageDateBlur,
  } = useStageDateEditing(extractedStagesData, { stages: allStages });
  const today = new Date().toISOString().split('T')[0];
  const clearStageDate = (stageName: string, field: 'start' | 'completion') =>
    setStageDates((prev) => ({
      ...prev,
      [stageName]: { ...(prev[stageName] || {}), [field]: '' },
    }));

  const { data: openCas } = useQuery({
    queryKey: ['corrective-actions', siteId],
    queryFn: async () => {
      const res = await api.get<{ corrective_actions: OpenCA[] }>(
        `/sites/${siteId}/corrective-actions`
      );
      return (res.data.corrective_actions || []).filter(
        (ca) => ca.status === 'open'
      );
    },
    enabled: !!siteId,
  });

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f) setFormFile(f);
    e.target.value = '';
  };

  const validCas = newCas.filter((ca) => ca.notes.trim() || ca.location.trim());
  const canSave = !!formFile && !busy && !done;

  const save = async (): Promise<string | null> => {
    if (!siteId || !formFile) return null;
    setBusy('saving');
    setError(null);
    try {
      const todayLocal = new Date().toLocaleDateString('en-CA');
      const created = await api.post<{ id: string }>('/inspections/', {
        site_id: siteId,
        type: 'routine',
        scheduled_date: new Date().toISOString(),
        conducted_date:
          !conductedDate || conductedDate === todayLocal
            ? new Date().toISOString()
            : `${conductedDate}T12:00:00Z`,
        notes: notes.trim() || undefined,
        findings: validCas.map((ca) => ({
          id: ca.id,
          category: 'manual_upload',
          item: 'Corrective action from uploaded form',
          condition: 'poor',
          description: ca.notes.trim() || ca.location.trim(),
          location: ca.location.trim() || 'Site',
          requires_correction: true,
          correction_deadline: ca.deadline
            ? `${ca.deadline}T12:00:00Z`
            : undefined,
          correction_notes: ca.notes.trim() || undefined,
          source: 'lite_manual_upload',
        })),
        checklist_responses: [],
        photos: [],
        construction_stage_id: selectedStageId || currentStage?.id || undefined,
        intake: 'manual_upload',
        idempotency_key: `manual-${siteId}-${Date.now()}`,
      });
      const inspectionId = created.data.id;

      const form = new FormData();
      form.append('file', formFile, formFile.name);
      await api.post(`/inspections/${inspectionId}/jurisdiction-report`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const followUps = buildLiteFollowUps({
        siteId,
        verifiedCaIds: resolvedIds,
        stageDates,
        stageNames: allStages.map((s) => s.stage_name),
        generateReport: false,
      });
      for (const step of followUps) {
        const url = withInspectionId(step.url, inspectionId);
        const body = withInspectionId(step.body ?? {}, inspectionId);
        if (step.method === 'PATCH') await api.patch(url, body);
        else await api.post(url, body);
      }

      await api.post(
        `/inspections/${inspectionId}/jurisdiction-report/complete`
      );
      setDone({ inspectionId });
      return inspectionId;
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not save the inspection — check the connection and try again.'
      );
      return null;
    } finally {
      setBusy(null);
    }
  };

  const sendToClient = async (inspectionId: string) => {
    setBusy('sending');
    setError(null);
    try {
      const r = await api.post<{ sent_count: number; failed_count: number }>(
        `/inspections/${inspectionId}/send-jurisdiction-report`
      );
      setDone({ inspectionId, sent: r.data });
    } catch {
      setError(
        'Saved, but the email did not go out — send it from the site page.'
      );
    } finally {
      setBusy(null);
    }
  };

  if (!siteId) {
    return (
      <div className="text-center py-12 text-slate-500">
        Site not found.{' '}
        <Link to="/lite/sites" className="text-primary-600">
          Back to sites
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto space-y-4 py-6 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
          Inspection form on file
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {site?.name} — the uploaded form, stage dates and corrective actions
          are recorded. No EnviroLink report was generated for this visit.
        </p>
        {done.sent ? (
          <p
            className="text-sm text-emerald-700 dark:text-emerald-300"
            data-testid="sent-summary"
          >
            Sent to {done.sent.sent_count} contact
            {done.sent.sent_count === 1 ? '' : 's'}
            {done.sent.failed_count
              ? ` (${done.sent.failed_count} failed)`
              : ''}
            .
          </p>
        ) : (
          <button
            type="button"
            onClick={() => sendToClient(done.inspectionId)}
            disabled={busy === 'sending'}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-xl font-medium"
          >
            {busy === 'sending' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send to Client (their form + our CA log)
          </button>
        )}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
        <button
          type="button"
          onClick={() => navigate(`/lite/sites/${siteId}`)}
          className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <Link
          to={`/lite/sites/${siteId}`}
          className="flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Link>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {site?.name ?? '…'}
        </span>
      </div>

      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">
          Upload an inspection form
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          For jurisdictions that require their own state or city form. Attach
          the completed form; the questions are skipped, but the stage dates and
          any corrective actions still go on the record.
        </p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Inspection date
        </span>
        <InspectionDateField
          id="manual-conducted-date"
          value={conductedDate}
          onChange={setConductedDate}
        />
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-amber-300 dark:border-amber-700 p-4 space-y-3">
        <h2 className="font-semibold text-slate-900 dark:text-white">
          The completed form
        </h2>
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={pickFile}
          data-testid="form-camera-input"
        />
        <input
          ref={fileRef}
          type="file"
          accept={FORM_ACCEPT}
          className="hidden"
          onChange={pickFile}
          data-testid="form-file-input"
        />
        {formFile ? (
          <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <FileText className="w-4 h-4 text-amber-600" />
            <span className="truncate" data-testid="form-file-name">
              {formFile.name}
            </span>
            <button
              type="button"
              onClick={() => setFormFile(null)}
              className="ml-auto text-xs underline text-slate-500"
            >
              change
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Photograph the signed form or attach the PDF. One file.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <Camera className="w-4 h-4" />
            Take photo
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
          >
            <Paperclip className="w-4 h-4" />
            Attach file
          </button>
        </div>
      </div>

      {allStages.length > 0 && (
        <StageTable
          allStages={allStages}
          currentStage={currentStage}
          stageDates={stageDates}
          setStageDates={setStageDates}
          today={today}
          handleStageDateBlur={handleStageDateBlur}
          handleClearStageDate={clearStageDate}
          handleStageDateSetAndSave={handleStageDateSetAndSave}
        />
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <h2 className="font-semibold text-slate-900 dark:text-white">
            Corrective actions
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Anything the form says must be fixed. Each one goes on the site's CA
            log and in the client email.
          </p>
        </div>
        <div className="p-4 space-y-3">
          {openCas && openCas.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Open from earlier visits
              </p>
              {openCas.map((ca) => {
                const resolved = ca.id in resolvedIds;
                return (
                  <label
                    key={ca.id}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={resolved}
                      onChange={(e) =>
                        setResolvedIds((prev) => {
                          const next = { ...prev };
                          if (e.target.checked) next[ca.id] = '';
                          else delete next[ca.id];
                          return next;
                        })
                      }
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      <span className="font-medium">
                        {ca.question_text ||
                          ca.action_description ||
                          'Corrective action'}
                      </span>
                      {(ca.location || ca.notes) && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          {[ca.location, ca.notes].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span className="block text-xs text-emerald-700 dark:text-emerald-300">
                        {resolved
                          ? 'Marked resolved on this visit'
                          : 'Still open'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {newCas.map((ca, i) => (
            <div
              key={ca.id}
              className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2"
              data-testid="new-ca"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  New corrective action {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setNewCas((prev) => prev.filter((c) => c.id !== ca.id))
                  }
                  aria-label="Remove corrective action"
                  className="text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                value={ca.location}
                onChange={(e) =>
                  setNewCas((prev) =>
                    prev.map((c) =>
                      c.id === ca.id ? { ...c, location: e.target.value } : c
                    )
                  )
                }
                placeholder="Location (e.g. north silt fence)"
                aria-label={`Corrective action ${i + 1} location`}
                className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
              />
              <textarea
                value={ca.notes}
                onChange={(e) =>
                  setNewCas((prev) =>
                    prev.map((c) =>
                      c.id === ca.id ? { ...c, notes: e.target.value } : c
                    )
                  )
                }
                rows={2}
                placeholder="What needs to be fixed"
                aria-label={`Corrective action ${i + 1} details`}
                className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
              />
              <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                Fix by
                <input
                  type="date"
                  onClick={openDatePicker}
                  value={ca.deadline}
                  onChange={(e) =>
                    setNewCas((prev) =>
                      prev.map((c) =>
                        c.id === ca.id ? { ...c, deadline: e.target.value } : c
                      )
                    )
                  }
                  className="text-xs px-2 py-1 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                />
              </label>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setNewCas((prev) => [...prev, newCa()])}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add a corrective action
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
        <label
          htmlFor="manual-notes"
          className="text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          Notes (optional)
        </label>
        <textarea
          id="manual-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="Anything worth remembering about this visit"
          className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <div className="fixed bottom-0 inset-x-0 bg-white/95 dark:bg-slate-900/95 border-t border-slate-200 dark:border-slate-700 p-3">
        <div className="max-w-2xl mx-auto flex gap-2">
          <button
            type="button"
            onClick={() => save()}
            disabled={!canSave}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium"
          >
            {busy === 'saving' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {formFile ? 'Save inspection' : 'Attach the form to continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
